import React, { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { Check, X, Eye, AlertCircle, Info, History, ChevronDown, ChevronRight, ClipboardCheck, Pencil, Save, RotateCcw, Clock, AlertTriangle, ShieldAlert, Timer, Search, Lightbulb, Calendar, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProjectSelector } from '@/components/ProjectSelector';
import { CostCentreSelector } from '@/components/CostCentreSelector';
import { RoleSelector } from '@/components/RoleSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import type { Timesheet } from '@/types/timesheet';
import { toast } from 'sonner';
import { AnomalyIndicator } from '@/components/AnomalyIndicator';
import { AnomalyDetectionService } from '@/services/anomalyDetectionService';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import { AuditHistoryDialog } from '@/components/AuditHistory';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { EmployeeImage } from '@/components/EmployeeImage';
import { ApprovalEmptyState } from '@/components/ApprovalEmptyState';
import { ApprovalGrid } from '@/components/ApprovalGrid';
import type { ApprovalRow } from '@/components/ApprovalGrid';
import { useCallback } from 'react';
import { SmartBanner, InlineBadge, WeeklyInsightCard, PatternReviewDialog } from '@/components/ApprovalAutomation';
import { useApprovalAutomation } from '@/hooks/use-approval-automation';
import { rejectionService } from '@/services/rejectionService';
import { useDataSource } from '@/contexts/DataSourceContext';
import { calculateEntryVariance, getVarianceBadgeVariant } from '@/services/varianceCalculationService';
import { BookingDetailsModal } from '@/components/ScheduledBookings';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi } from '@/services/timesheetApi';
import { AbsenceIndicator } from '@/components/AbsenceBadge';
import type { AbsenceType } from '@/components/AbsenceBadge';
import { consolidateAnomalies, shouldConsolidate } from '@/services/anomalyConsolidationService';
import { auditService } from '@/services/auditService';
import type { AuditChange } from '@/types/audit';
import type { ConsolidatedAnomaly } from '@/services/anomalyConsolidationService';
import { OvertimeTypeBadge } from '@/components/timesheet/OvertimeTypeBadge';

import type { PolicyViolation } from '@/data/mockPolicyViolations';
import { getViolationsForEmployee } from '@/data/mockPolicyViolations';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import { useSettings } from '@/contexts/SettingsContext';
import { calculateViolationsForTimesheets, getHighestSeverity } from '@/services/policyViolationService';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { cn } from '@/lib/utils';
import { AIInsightsSheet } from '@/components/AIAssistant/AIInsightsSheet';
import type { AnomalyWarning, PersonalInsight } from '@/services/aiService';
import { aiService } from '@/services/aiService';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { DashboardLoadingState } from '@/components/DashboardLoadingState';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { delegationService } from '@/features/delegation/services/delegationService';
import type { ApprovalDelegation } from '@/features/delegation/types/delegation.types';

// ── Days / hours helpers (mirrors TimesheetGrid) ─────────────────────────────
const ALL_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const ALL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildDaysFromStart(
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  workingDays?: Record<string, { isWorkingDay: boolean }>
) {
  const days: typeof ALL_DAYS[number][] = [];
  const labels: string[] = [];
  for (let i = 0; i < 7; i++) {
    const idx = (weekStartDay + i) % 7;
    const day = ALL_DAYS[idx];
    if (workingDays) {
      if (workingDays[day]?.isWorkingDay) {
        days.push(day);
        labels.push(ALL_DAY_LABELS[idx]);
      }
    } else if (day !== 'saturday' && day !== 'sunday') {
      days.push(day);
      labels.push(ALL_DAY_LABELS[idx]);
    }
  }
  return { days, labels };
}

function isWeekendDay(day: typeof ALL_DAYS[number]): boolean {
  return day === 'saturday' || day === 'sunday';
}

// Helper function to generate a random photo URL for employees without proper avatars
const getDemoPhotoUrl = (name: string, id: number): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

// Helper function to check if URL is an Evo avatar (not from dicebear or other non-Evo sources)
const isEvoAvatarUrl = (url: string): boolean => {
  const trimmedUrl = url.trim().toLowerCase();
  if (trimmedUrl.includes('dicebear')) {
    return false;
  }
  return true;
};

// Helper function to get photo URL - uses Evo avatar if available, otherwise random image
const getPhotoUrl = (name: string, id: number, photoUrl?: string | null): string => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return getDemoPhotoUrl(name, id);
};

// Helper function to get the real Employee.PhotoUrl if available, returns undefined otherwise.
// Used in live mode with raw API data (bypassing ensureEmployeeAvatars) so no URL filtering
// is needed — the value is exactly what the backend stores. EmployeeImage shows initials
// as fallback when photoUrl is undefined.
const getLivePhotoUrl = (photoUrl?: string | null): string | undefined => {
  if (photoUrl && photoUrl.trim().length > 0) {
    return photoUrl;
  }
  return undefined;
};

// Constants
const MAX_SELECTION_LIMIT = 500;
const BULK_CONFIRMATION_THRESHOLD = 50;

// ─── F-04 helper component ─────────────────────────────────────────────────
// Coloured pill showing the current workflow stage name + optional progress
// indicator (e.g. "Stage 2 of 3"). Colour cycles through a set of distinct
// hues so different stage names are visually distinguishable at a glance.
const STAGE_COLOURS = [
  'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300',
  'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300',
  'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300',
  'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300',
  'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300',
];

function stageColour(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return STAGE_COLOURS[hash % STAGE_COLOURS.length];
}

// ── WaitingBadge ─────────────────────────────────────────────────────────────
// Colour-coded waiting duration: green <3d | amber 3-7d | red >7d
function WaitingBadge({ submittedAt }: { submittedAt?: Date | null }) {
  if (!submittedAt) return <span className="text-xs text-muted-foreground">—</span>;

  const totalDays = Math.floor((Date.now() - submittedAt.getTime()) / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;

  let label: string;
  if (totalDays === 0) label = '< 1d';
  else if (weeks === 0) label = `${days}d`;
  else if (days === 0) label = `${weeks}w`;
  else label = `${weeks}w ${days}d`;

  const colour =
    totalDays > 7  ? 'text-red-600 dark:text-red-400 font-semibold' :
    totalDays >= 3 ? 'text-amber-600 dark:text-amber-400 font-medium' :
                     'text-green-600 dark:text-green-500';

  return <span className={`text-xs tabular-nums ${colour}`}>{label}</span>;
}

function StageBadge({ name, order, total }: { name: string; order?: number; total?: number }) {
  const colour = stageColour(name);
  const progress = order != null && total != null ? ` (${order}/${total})` : '';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colour} whitespace-nowrap`}>
      {name}{progress}
    </span>
  );
}

// ─── F-02 helper component ─────────────────────────────────────────────────
// Renders the list of anomaly + policy-violation issues inside the flagged-
// approval confirmation dialog. Defined outside ApprovalDashboard so it is
// a stable component reference and avoids JSX-in-IIFE parse issues.
function FlaggedIssuesList({
  anomalyResult,
  policyViolations,
}: {
  anomalyResult: AnomalyDetectionResult | undefined;
  policyViolations: PolicyViolation[];
}) {
  const hasAnomalies = anomalyResult?.hasAnomalies && (anomalyResult.anomalies?.length ?? 0) > 0;
  const hasViolations = policyViolations.length > 0;
  if (!hasAnomalies && !hasViolations) return null;

  return (
    <div className="my-2 max-h-[260px] overflow-y-auto space-y-3">
      {hasAnomalies && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Detected Anomalies
          </div>
          <div className="space-y-1.5">
            {anomalyResult!.anomalies.map((anomaly, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{anomaly.message}</span>
                  {anomaly.severity && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-xs ${
                        anomaly.severity === 'high'
                          ? 'border-red-400 text-red-700 dark:text-red-400'
                          : anomaly.severity === 'medium'
                          ? 'border-amber-400 text-amber-700 dark:text-amber-400'
                          : 'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {anomaly.severity}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasViolations && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Policy Violations
          </div>
          <div className="space-y-1.5">
            {policyViolations.map((violation, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
              >
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{violation.message}</span>
                  {violation.severity && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-xs ${
                        violation.severity === 'high'
                          ? 'border-red-400 text-red-700 dark:text-red-400'
                          : violation.severity === 'medium'
                          ? 'border-amber-400 text-amber-700 dark:text-amber-400'
                          : 'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {violation.severity}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ──────────────────────────────────────────────────────────────────────────

export function ApprovalDashboard() {
  const { timesheets, employees, projects, currentEmployee, updateTimesheet } = useTimesheetForScreen('approvals');
  const { getScreenMode } = useDataSource();
  const { canViewApprovalDashboard, canViewApprovals, canApproveReject, canBulkApprove, canEditTimesheetInApproval, canDelegateApproval, requiresRejectionReason, canViewPolicyViolations, canViewDetectedIssues } = usePermissions();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedRows, setSelectedRows] = useState<ApprovalRow[]>([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [viewingTimesheet, setViewingTimesheet] = useState<Timesheet | null>(null);
  const [rejectingTimesheet, setRejectingTimesheet] = useState<Timesheet | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const REJECTION_MIN_CHARS = 20;
  const REJECTION_MAX_CHARS = 500;
  const trimmedRejectionReason = rejectionReason.trim();
  const rejectionCharCount = rejectionReason.length;
  const rejectionMeetsMinimum = trimmedRejectionReason.length >= REJECTION_MIN_CHARS;
  const rejectionCharsRemaining = REJECTION_MIN_CHARS - trimmedRejectionReason.length;
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [anomalyFilter, setAnomalyFilter] = useState<string>('all');
  const [overdueFilter, setOverdueFilter] = useState<boolean>(false);
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<'approve' | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [pendingFlaggedApproval, setPendingFlaggedApproval] = useState<Timesheet | null>(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<number>>(new Set());
  const [viewingBookingDetails, setViewingBookingDetails] = useState<any | null>(null);
  const [showIssuesSheet, setShowIssuesSheet] = useState(false);

  // Week start day from Settings (drives column order, mirrors TimesheetGrid)
  const { weekStartDay, organizationSettings } = useSettings();

  // Edit mode state for the View Timesheet dialog
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEntries, setEditedEntries] = useState<Record<number, Partial<{ projectId: string; costCentreId: string; description: string; roleId: number | undefined; roleName: string | undefined; hours: Timesheet['entries'][0]['hours'] }>>>({});
  // Two-phase editing buffer — raw string while the user is typing, committed on blur
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const getHoursCellKey = (entryId: number, day: typeof ALL_DAYS[number]) => `${entryId}-${day}`;
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<(() => void) | null>(null);

  // Server-side pending approvals state (for live mode)
  const [serverPendingTimesheets, setServerPendingTimesheets] = useState<Timesheet[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);

  // Raw employee photo URLs fetched directly from API (bypasses ensureEmployeeAvatars which
  // replaces null/empty photoUrls with picsum mock URLs). This mirrors how the Missing
  // Timesheets grid gets photoUrl directly from its API response.
  const [rawEmployeePhotoUrls, setRawEmployeePhotoUrls] = useState<Map<number, string | undefined>>(new Map());

  // Active delegations — used to tag rows with "Delegated From" manager name
  const [activeDelegations, setActiveDelegations] = useState<ApprovalDelegation[]>([]);
  
  // Get current screen mode
  const screenMode = getScreenMode('approvals');

  // Get organization settings for policy violation calculations
  const { settings: orgSettings, loading: settingsLoading, error: settingsError } = useOrganisationSettings('approvals');

  // Fetch overtime types from the OvertimeTypes API instead of organizationSettings
  const overtimeEnabledForApprovals = organizationSettings?.overtime?.enabled === true;
  const { activeTypes: apiActiveOvertimeTypes } = useOvertimeTypes({ enabled: overtimeEnabledForApprovals, screenId: 'approvals' });

  // When a timesheet review dialog opens, honour the org setting for whether
  // the issues panel should be expanded by default.
  useEffect(() => {
    if (viewingTimesheet) {
      setShowIssuesSheet(orgSettings.approvalScreen?.panelDefaultExpanded ?? false);
    } else {
      setShowIssuesSheet(false);
    }
  }, [viewingTimesheet, orgSettings.approvalScreen?.panelDefaultExpanded]);

  // Approval Automation
  const [reviewingPattern, setReviewingPattern] = useState<string | null>(null);
  const automation = useApprovalAutomation(timesheets, employees, currentEmployee.id);

  // Detect patterns on mount and when timesheets change
  useEffect(() => {
    automation.detectPatterns();
  }, [timesheets.length]);

  // Fetch active delegations (delegated-to-me) for queue segmentation (FE-14)
  useEffect(() => {
    delegationService
      .getDelegatedToMe()
      .then((delegations) => {
        setActiveDelegations(delegations.filter((d) => d.status === 'active'));
      })
      .catch(() => {
        // Non-critical — fall back to unsegmented view
        setActiveDelegations([]);
      });
  }, []);

  // Fetch pending approvals and raw employee photo URLs from API in live mode
  useEffect(() => {
    if (screenMode === 'live') {
      setIsLoadingPending(true);
      Promise.all([
        approvalsApi.getPendingApprovals(),
        timesheetApi.getAllEmployees(),
      ])
        .then(([pendingResult, rawEmployees]) => {
          setServerPendingTimesheets(pendingResult);
          // Build a map of employeeId -> raw photoUrl (unprocessed by ensureEmployeeAvatars)
          const photoMap = new Map<number, string | undefined>();
          rawEmployees.forEach(emp => photoMap.set(emp.id, emp.photoUrl));
          setRawEmployeePhotoUrls(photoMap);
        })
        .catch((error) => {
          console.error('Failed to fetch pending approvals:', error);
          toast.error('Failed to load pending approvals');
        })
        .finally(() => setIsLoadingPending(false));
    }
  }, [screenMode]);

  // Listen for company-changed event to refetch data when switching companies
  // This ensures data is fetched from the correct integration source (local DB vs external API)
  useEffect(() => {
    const handleCompanyChange = () => {
      if (screenMode === 'live') {
        setIsLoadingPending(true);
        Promise.all([
          approvalsApi.getPendingApprovals(),
          timesheetApi.getAllEmployees(),
        ])
          .then(([pendingResult, rawEmployees]) => {
            setServerPendingTimesheets(pendingResult);
            const photoMap = new Map<number, string | undefined>();
            rawEmployees.forEach(emp => photoMap.set(emp.id, emp.photoUrl));
            setRawEmployeePhotoUrls(photoMap);
          })
          .catch((error) => {
            console.error('Failed to fetch pending approvals after company change:', error);
            toast.error('Failed to load pending approvals');
          })
          .finally(() => setIsLoadingPending(false));
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [screenMode]);

  // Helper function to refresh pending approvals in live mode
  const refreshPendingApprovals = async () => {
    if (screenMode === 'live') {
      try {
        const approvals = await approvalsApi.getPendingApprovals();
        setServerPendingTimesheets(approvals);
      } catch (error) {
        console.error('Failed to refresh pending approvals:', error);
      }
    }
  };

  // Get timesheets assigned to current user as approver - workflow-aware filtering
  // In live mode, use server-side filtered data; in demo mode, use client-side filtering
  const pendingTimesheets = useMemo(() => {
    if (screenMode === 'live') {
      // Use server-side filtered data in live mode
      return serverPendingTimesheets;
    }
    // Client-side filtering for demo mode.
    // Include:
    //  (a) timesheets directly assigned to the current user as approver
    //  (b) timesheets assigned to any manager who has an active delegation to the
    //      current user — these will be tagged via delegationManagerId on approvalRows
    //      and routed to a segmented section by SegmentedApprovalsQueue.
    const delegatedManagerIds = new Set(
      activeDelegations
        .filter((d) => d.status === 'active')
        .map((d) => String(d.managerId))
    );

    return timesheets.filter(ts => {
      // Only show submitted (pending) timesheets
      if (ts.status !== 'submitted') return false;
      
      // (a) Own approvals
      if (ts.approverId === currentEmployee.id) return true;

      // (b) Delegated approvals — ts.approverId belongs to a delegating manager
      const tsApproverId = ts.delegatedApproverId ?? ts.approverId;
      if (tsApproverId !== undefined && delegatedManagerIds.has(String(tsApproverId))) {
        return true;
      }

      return false;
    });
  }, [screenMode, serverPendingTimesheets, timesheets, currentEmployee, activeDelegations]);

  // Calculate anomalies for all timesheets
  // Pass organization settings to use settings-based limits instead of hardcoded defaults
  const timesheetAnomalies = useMemo(() => {
    const anomalyMap = new Map<number, AnomalyDetectionResult>();
    
    pendingTimesheets.forEach(timesheet => {
      const employee = employees.find(e => e.id === timesheet.employeeId);
      if (!employee) return;

      // Calculate historical data for the employee
      const historicalData = AnomalyDetectionService.calculateHistoricalData(
        timesheet.employeeId,
        timesheets
      );

      // Detect anomalies - pass orgSettings to use settings-based limits
      const anomalyResult = AnomalyDetectionService.detectAnomalies(
        timesheet,
        employee,
        historicalData,
        timesheets,
        orgSettings
      );

      anomalyMap.set(timesheet.id, anomalyResult);
    });

    return anomalyMap;
  }, [pendingTimesheets, employees, timesheets, orgSettings]);

  // Calculate policy violations for all timesheets
  // In live mode: use backend settings to calculate violations
  // In demo mode: use mock data
  const timesheetPolicyViolations = useMemo(() => {
    const violationsMap = new Map<number, PolicyViolation[]>();
    
    if (screenMode === 'live') {
      // Live mode: calculate violations from backend settings
      // Also skip if settings fetch failed — avoids using default settings
      // that would re-enable all disabled policy checks
      if (settingsLoading || !orgSettings || settingsError) {
        return violationsMap;
      }
      return calculateViolationsForTimesheets(pendingTimesheets, orgSettings);
    } else {
      // Demo mode: use mock data
      pendingTimesheets.forEach(ts => {
        const violations = getViolationsForEmployee(ts.employeeId);
        if (violations.length > 0) {
          violationsMap.set(ts.id, violations);
        }
      });
      return violationsMap;
    }
  }, [pendingTimesheets, orgSettings, settingsLoading, settingsError, screenMode]);

  // Helper function to calculate timesheet total hours
  const calculateTimesheetTotal = (timesheet: Timesheet) => {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  };

  // Apply filters
  const filteredTimesheets = useMemo(() => {
    const filtered = pendingTimesheets.filter(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      
      const matchesSearch = searchTerm === '' || 
        ts.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDepartment = departmentFilter === 'all' || 
        employee?.department === departmentFilter;

      // Check if timesheet has any issues (anomalies or policy violations)
      const hasIssues = anomalyResult?.hasAnomalies || policyViolations.length > 0;
      const matchesAnomaly = anomalyFilter === 'all' || 
        (anomalyFilter === 'with-anomalies' && hasIssues) ||
        (anomalyFilter === 'clean' && !hasIssues);

      // Check overdue filter (>7 days = overdue per PRD F-05)
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      const isOverdue = daysSinceSubmission > 7;
      const matchesOverdue = !overdueFilter || isOverdue;

      const passes = matchesSearch && matchesDepartment && matchesAnomaly && matchesOverdue;

      return passes;
    });

    return filtered;
  }, [pendingTimesheets, searchTerm, departmentFilter, anomalyFilter, overdueFilter, employees, timesheetAnomalies, timesheetPolicyViolations]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const pending = pendingTimesheets.length;
    const overdue = pendingTimesheets.filter(ts => {
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return daysSinceSubmission > 7;
    }).length;
    
    const totalHours = pendingTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }, 0);
    }, 0);

    // Calculate anomaly and policy violation stats
    let withAnomalies = 0;
    let highSeverity = 0;
    let mediumSeverity = 0;
    let lowSeverity = 0;

    pendingTimesheets.forEach(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      
      // Check for anomalies or policy violations
      const hasAnomalies = anomalyResult?.hasAnomalies;
      const hasPolicyViolations = policyViolations.length > 0;
      
      if (hasAnomalies || hasPolicyViolations) {
        withAnomalies++;
        
        // Check anomaly severities
        const hasHighAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'high') ?? false;
        const hasMediumAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'medium') ?? false;
        const hasLowAnomaly = anomalyResult?.anomalies?.some(a => a.severity === 'low') ?? false;
        
        // Check policy violation severities
        const hasHighViolation = policyViolations.some(v => v.severity === 'high');
        const hasMediumViolation = policyViolations.some(v => v.severity === 'medium');
        const hasLowViolation = policyViolations.some(v => v.severity === 'low');
        
        // Combine both sources
        if (hasHighAnomaly || hasHighViolation) highSeverity++;
        else if (hasMediumAnomaly || hasMediumViolation) mediumSeverity++;
        else if (hasLowAnomaly || hasLowViolation) lowSeverity++;
      }
    });

    return { pending, overdue, totalHours, withAnomalies, highSeverity, mediumSeverity, lowSeverity };
  }, [pendingTimesheets, timesheetAnomalies, timesheetPolicyViolations]);

  const departments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department));
    return Array.from(depts);
  }, [employees]);

  // When filters change and selected rows are no longer visible, clear stale selection
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visibleIds = new Set(filteredTimesheets.map(ts => ts.id));
    const hasStale = Array.from(selectedIds).some(id => !visibleIds.has(id));
    if (hasStale) {
      setClearSelection(true);
    }
  }, [filteredTimesheets]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection — driven by DataTableEnhanced via onSelectionChange ──────────
  const handleSelectionChange = useCallback((rows: ApprovalRow[]) => {
    setSelectedRows(rows);
    setSelectedIds(new Set(rows.map(r => r.timesheet.id)));
    // Reset the clearSelection trigger after it fires so it can fire again next time
    setClearSelection(false);
  }, []);

  const approveTimesheet = async (timesheet: Timesheet, notes?: string) => {
    // Guard: block self-approval when cannotApproveOwnTimesheet is set
    if (timesheet.cannotApproveOwnTimesheet === true && timesheet.employeeId === currentEmployee.id) {
      toast.error('Cannot approve own timesheet');
      return;
    }

    if (screenMode === 'live') {
      try {
        const notesToSend = notes?.trim() || undefined;
        const response = await approvalsApi.approveTimesheet(timesheet.id, notesToSend);

        // Show appropriate message based on workflow status
        if (response.workflowCompleted && response.finalStatus === 'approved') {
          toast.success(`Timesheet for ${timesheet.employeeName} fully approved`);
        } else if (response.advancedToNextStage) {
          toast.success(`Approved stage for ${timesheet.employeeName}. ${response.newStageName ? `Now at: ${response.newStageName}` : ''}`);
        } else {
          toast.success(`Approved timesheet for ${timesheet.employeeName}`);
        }
        
        // Refresh pending approvals list
        refreshPendingApprovals();
      } catch (error) {
        console.error('Failed to approve timesheet:', error);
        toast.error('Failed to approve timesheet');
      }
    } else {
      // Demo mode logic
      const updated = {
        ...timesheet,
        status: 'approved' as const,
        approvedAt: new Date(),
        approverId: currentEmployee.id,
        approverName: currentEmployee.name
      };
      updateTimesheet(updated);

      // Log audit entry for demo mode, including delegation context when applicable
      delegationService.getDelegatedToMe().then((delegatedToMe) => {
        const activeDelegation = delegatedToMe.find(d => d.status === 'active');
        auditService.logAction(
          timesheet.id,
          'approved',
          currentEmployee.id,
          currentEmployee.name,
          {
            actorRole: 'approver',
            previousStatus: 'submitted',
            newStatus: 'approved',
            isDelegated: !!activeDelegation,
            delegatedFromManagerName: activeDelegation?.managerName,
            delegatedFromManagerId: activeDelegation?.managerId,
          }
        );
      }).catch(() => {
        // Audit log failure should not affect UX — log silently
        auditService.logAction(
          timesheet.id,
          'approved',
          currentEmployee.id,
          currentEmployee.name,
          { actorRole: 'approver', previousStatus: 'submitted', newStatus: 'approved' }
        );
      });

      toast.success(`Approved timesheet for ${timesheet.employeeName}`);
    }
  };

  // Intercept single-item approve: show confirmation dialog if the timesheet has flags,
  // otherwise call approveTimesheet() directly to preserve the fast one-click path.
  const handleApproveClick = (timesheet: Timesheet, opts?: { fromViewDialog?: boolean }) => {
    const anomalyResult = timesheetAnomalies.get(timesheet.id);
    const policyViolations = timesheetPolicyViolations.get(timesheet.id) || [];
    // Only treat as flagged for issue types the current user has permission to see.
    // This prevents the confirmation dialog appearing for issues that are invisible to them.
    const isFlagged =
      (canViewDetectedIssues() && anomalyResult?.hasAnomalies === true) ||
      (canViewPolicyViolations() && policyViolations.length > 0);

    if (isFlagged) {
      // Close the view dialog first so the AlertDialog sits on top cleanly
      if (opts?.fromViewDialog) {
        setViewingTimesheet(null);
      }
      setPendingFlaggedApproval(timesheet);
    } else {
      if (opts?.fromViewDialog) {
        setViewingTimesheet(null);
      }
      approveTimesheet(timesheet);
    }
  };

  const handleBulkApproveClick = () => {
    const toApprove = selectedRows.map(r => r.timesheet);

    // Show confirmation for large selections or items with anomalies
    const itemsWithAnomalies = toApprove.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });

    if (toApprove.length >= BULK_CONFIRMATION_THRESHOLD || itemsWithAnomalies.length > 0) {
      setPendingBulkAction('approve');
      setShowBulkConfirmDialog(true);
    } else {
      executeBulkApprove();
    }
  };

  const executeBulkApprove = async () => {
    const allSelected = selectedRows.map(r => r.timesheet);
    // Filter out own timesheets where self-approval is blocked
    const toApprove = allSelected.filter(
      ts => !(ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployee.id)
    );
    const skippedCount = allSelected.length - toApprove.length;
    const notes = bulkApprovalNotes;

    // Show progress for large batches
    if (toApprove.length > 20) {
      toast.loading(`Approving ${toApprove.length} timesheets...`, {
        id: 'bulk-approve',
      });
    }

    for (const ts of toApprove) {
      await approveTimesheet(ts, notes);
    }

    // Clear TanStack selection
    setClearSelection(true);

    // Dismiss loading toast and show success
    toast.dismiss('bulk-approve');
    if (toApprove.length > 0) {
      toast.success(`Successfully approved ${toApprove.length} timesheets`);
    }
    if (skippedCount > 0) {
      toast.warning(`${skippedCount} own timesheet${skippedCount !== 1 ? 's' : ''} skipped (cannot approve own)`);
    }

    setShowBulkConfirmDialog(false);
    setPendingBulkAction(null);
    setBulkApprovalNotes('');
  };

  // F-12: Bulk Reject
  const BULK_REJECT_MIN_CHARS = 20;
  const BULK_REJECT_MAX_CHARS = 500;
  const trimmedBulkRejectReason = bulkRejectReason.trim();
  const bulkRejectMeetsMinimum = trimmedBulkRejectReason.length >= BULK_REJECT_MIN_CHARS;
  const bulkRejectCharsRemaining = BULK_REJECT_MIN_CHARS - trimmedBulkRejectReason.length;

  const handleBulkRejectClick = () => {
    if (selectedRows.length > 0) {
      setShowBulkRejectDialog(true);
    }
  };

  const executeBulkReject = async () => {
    if (requiresRejectionReason() && !bulkRejectMeetsMinimum) return;

    const allSelected = selectedRows.map(r => r.timesheet);
    // Filter out own timesheets where self-rejection is blocked
    const toReject = allSelected.filter(
      ts => !(ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployee.id)
    );
    const skippedCount = allSelected.length - toReject.length;
    const reason = trimmedBulkRejectReason;

    if (toReject.length > 5) {
      toast.loading(`Rejecting ${toReject.length} timesheets...`, { id: 'bulk-reject' });
    }

    try {
      for (const ts of toReject) {
        if (screenMode === 'live') {
          await approvalsApi.rejectTimesheet(ts.id, reason);
        } else {
          const updated = {
            ...ts,
            status: 'rejected' as const,
            rejectedAt: new Date(),
            rejectionReason: reason,
            approverId: currentEmployee.id,
            approverName: currentEmployee.name
          };
          updateTimesheet(updated);
        }
      }

      if (screenMode === 'live') {
        refreshPendingApprovals();
      }

      toast.dismiss('bulk-reject');
      if (toReject.length > 0) {
        toast.success(`${toReject.length} timesheet${toReject.length !== 1 ? 's' : ''} rejected.`);
      }
      if (skippedCount > 0) {
        toast.warning(`${skippedCount} own timesheet${skippedCount !== 1 ? 's' : ''} skipped (cannot reject own)`);
      }
    } catch (error) {
      console.error('Failed to bulk reject timesheets:', error);
      toast.dismiss('bulk-reject');
      toast.error('Failed to reject one or more timesheets');
    } finally {
      setClearSelection(true);
      setShowBulkRejectDialog(false);
      setBulkRejectReason('');
    }
  };

  // Get selected timesheets for confirmation dialog — derived from TanStack selection
  const selectedTimesheets = useMemo(() => {
    return selectedRows.map(r => r.timesheet);
  }, [selectedRows]);

  const selectedWithAnomalies = useMemo(() => {
    return selectedTimesheets.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });
  }, [selectedTimesheets, timesheetAnomalies]);

  const rejectTimesheet = async () => {
    // Guard: block self-rejection when cannotApproveOwnTimesheet is set
    if (rejectingTimesheet?.cannotApproveOwnTimesheet === true && rejectingTimesheet?.employeeId === currentEmployee.id) {
      toast.error('Cannot reject own timesheet');
      setRejectingTimesheet(null);
      setRejectionReason('');
      return;
    }

    if (!rejectingTimesheet || rejectionCharCount === 0 || (requiresRejectionReason() && !rejectionMeetsMinimum)) {
      toast.error(rejectionCharCount === 0
        ? 'Please provide a rejection reason'
        : `Please provide a rejection reason (minimum ${REJECTION_MIN_CHARS} characters)`);
      return;
    }

    const reasonToSubmit = trimmedRejectionReason;

    try {
      if (screenMode === 'live') {
        // Use dedicated API endpoint in live mode
        await approvalsApi.rejectTimesheet(rejectingTimesheet.id, reasonToSubmit);
        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
        // Refresh pending approvals list
        refreshPendingApprovals();
      } else {
        // Existing demo mode logic
        const updated = {
          ...rejectingTimesheet,
          status: 'rejected' as const,
          rejectedAt: new Date(),
          rejectionReason: reasonToSubmit,
          approverId: currentEmployee.id,
          approverName: currentEmployee.name
        };
        updateTimesheet(updated);

        // Record rejection in RejectionRecord table
        rejectionService.setMode(false);
        
        await rejectionService.recordRejection(
          rejectingTimesheet,
          reasonToSubmit,
          currentEmployee.id,
          currentEmployee.name,
          projects,
          [], // costCentres - not needed for basic rejection tracking
          {
            approverRole: currentEmployee.role || 'manager'
          }
        );

        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
      }
    } catch (error) {
      console.error('Failed to reject timesheet:', error);
      toast.error('Failed to reject timesheet');
    } finally {
      // Always close the modal and clear the reason so the user is not stuck
      setRejectingTimesheet(null);
      setRejectionReason('');
    }
  };

  const toggleEntryExpansion = (entryId: number) => {
    const newSet = new Set(expandedEntryIds);
    if (newSet.has(entryId)) {
      newSet.delete(entryId);
    } else {
      newSet.add(entryId);
    }
    setExpandedEntryIds(newSet);
  };

  const shouldShowExpansionIcon = (description: string) => {
    return description && description.length > 100;
  };

  // Days array driven by org settings + week start day (mirrors TimesheetGrid)
  const { days: DAYS, labels: DAY_LABELS } = useMemo(() => {
    const workingDays = orgSettings?.workingProfile?.workingDays
      ?? DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays;
    return buildDaysFromStart(weekStartDay, workingDays);
  }, [weekStartDay, orgSettings?.workingProfile?.workingDays]);

  // Reset edit state whenever the viewed timesheet changes
  useEffect(() => {
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
    setIsSavingEdits(false);
  }, [viewingTimesheet?.id]);

  // Check if there are any unsaved edits
  const hasUnsavedChanges = Object.keys(editedEntries).length > 0;

  // Handle a field change for a specific entry
  const handleEntryFieldChange = (
    entryId: number,
    field: 'projectId' | 'costCentreId' | 'description',
    value: string
  ) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], [field]: value },
    }));
  };

  // Handle a role change for a specific entry
  const handleEntryRoleChange = (entryId: number, roleId: number | undefined, roleName?: string) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], roleId, roleName },
    }));
  };

  // Handle an hours change for a specific entry and day
  const handleEntryHoursChange = (
    entryId: number,
    day: keyof Timesheet['entries'][0]['hours'],
    value: string
  ) => {
    const parsed = parseFloat(value);
    const numericValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
    setEditedEntries(prev => {
      const existing = prev[entryId] || {};
      const currentEntry = viewingTimesheet?.entries.find(e => e.id === entryId);
      const baseHours = existing.hours ?? currentEntry?.hours ?? { monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0 };
      return {
        ...prev,
        [entryId]: {
          ...existing,
          hours: { ...baseHours, [day]: numericValue },
        },
      };
    });
  };

  // When project changes, clear the cost centre since it may no longer be valid
  const handleEntryProjectChange = (entryId: number, projectId: string) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], projectId, costCentreId: '' },
    }));
  };

  // Attempt to close the dialog — guard against unsaved changes
  const handleDialogCloseAttempt = (closeAction: () => void) => {
    if (isEditMode && hasUnsavedChanges) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedChangesDialog(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard and execute the pending close action
  const handleConfirmDiscard = () => {
    setShowUnsavedChangesDialog(false);
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  // Cancel edit mode without saving
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
  };

  // Save all edits to the timesheet
  const handleSaveEdits = async () => {
    if (!viewingTimesheet) return;
    setIsSavingEdits(true);
    try {
      // Build the updated entries by merging edits over originals
      const updatedEntries = viewingTimesheet.entries.map(entry => {
        const edits = editedEntries[entry.id];
        if (!edits) return entry;
        return {
          ...entry,
          projectId: edits.projectId ?? entry.projectId,
          costCentreId: edits.costCentreId ?? entry.costCentreId,
          description: edits.description ?? entry.description,
          roleId: 'roleId' in edits ? edits.roleId : entry.roleId,
          roleName: 'roleName' in edits ? edits.roleName : entry.roleName,
          hours: edits.hours ?? entry.hours,
        };
      });

      const updatedTimesheet: Timesheet = {
        ...viewingTimesheet,
        entries: updatedEntries,
      };

      if (screenMode === 'live') {
        await timesheetApi.updateTimesheet(viewingTimesheet.id, updatedTimesheet);
      }

      // Build field-level changes for audit trail
      const auditChanges: AuditChange[] = [];
      for (const entry of viewingTimesheet.entries) {
        const edits = editedEntries[entry.id];
        if (!edits) continue;
        if (edits.projectId != null && edits.projectId !== entry.projectId) {
          auditChanges.push({ field: 'project', oldValue: entry.projectId, newValue: edits.projectId, entryId: entry.id });
        }
        if (edits.costCentreId != null && edits.costCentreId !== entry.costCentreId) {
          auditChanges.push({ field: 'costCentre', oldValue: entry.costCentreId, newValue: edits.costCentreId, entryId: entry.id });
        }
        if (edits.description != null && edits.description !== entry.description) {
          auditChanges.push({ field: 'description', oldValue: entry.description, newValue: edits.description, entryId: entry.id });
        }
        if ('roleId' in edits && edits.roleId !== entry.roleId) {
          auditChanges.push({ field: 'role', oldValue: entry.roleName, newValue: edits.roleName, entryId: entry.id });
        }
        if (edits.hours) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
          for (const day of days) {
            if (edits.hours[day] !== undefined && edits.hours[day] !== entry.hours[day]) {
              auditChanges.push({ field: `hours_${day}`, oldValue: entry.hours[day], newValue: edits.hours[day], entryId: entry.id });
            }
          }
        }
      }

      // Log audit trail
      if (auditChanges.length > 0) {
        if (screenMode === 'demo') {
          auditService.logAction(
            viewingTimesheet.id,
            'approver_edited',
            currentEmployee?.id ?? 'unknown',
            currentEmployee?.name ?? 'Approver',
            {
              actorRole: 'approver',
              changes: auditChanges,
              comment: 'Edited during approval review',
            }
          );
        } else {
          // In live mode, record the audit event via the backend endpoint
          try {
            await approvalsApi.recordApproverEdit(viewingTimesheet.id, {
              comment: 'Edited during approval review',
              changes: auditChanges.map(c => ({
                entryId: c.entryId,
                field: c.field,
                oldValue: c.oldValue != null ? String(c.oldValue) : undefined,
                newValue: c.newValue != null ? String(c.newValue) : undefined,
              })),
            });
          } catch (auditError) {
            // Audit logging failure should not block the save
            console.error('Failed to record approver edit audit event:', auditError);
          }
        }
      }

      // Always update local context state (works for both demo and live)
      updateTimesheet(updatedTimesheet);
      setViewingTimesheet(updatedTimesheet);
      setIsEditMode(false);
      setEditedEntries({});
      setEditingHours({});
      toast.success('Timesheet entries updated successfully');
    } catch (error) {
      console.error('Failed to save timesheet edits:', error);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSavingEdits(false);
    }
  };

  // Compute live daily totals from either edited or original hours
  const computedDailyTotals = useMemo(() => {
    if (!viewingTimesheet) return null;
    const totals = { monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0 };
    viewingTimesheet.entries.forEach(entry => {
      const editedHours = editedEntries[entry.id]?.hours;
      const hours = editedHours ?? entry.hours;
      (Object.keys(totals) as Array<keyof typeof totals>).forEach(day => {
        totals[day] += hours[day] ?? 0;
      });
    });
    return totals;
  }, [viewingTimesheet, editedEntries]);

  // ── Build flat rows for ApprovalGrid ────────────────────────────────────
  // Also enrich rows with delegation metadata so SegmentedApprovalsQueue can
  // split them and ApprovalGrid can show the "via delegation" label.
  const approvalRows: ApprovalRow[] = useMemo(() => {
    return filteredTimesheets.map(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      const anomalyResult = timesheetAnomalies.get(ts.id);
      const policyViolations = timesheetPolicyViolations.get(ts.id) || [];
      const totalHours = calculateTimesheetTotal(ts);
      const daysSince = ts.submittedAt
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      const isOverdue = daysSince > 7;
      const photoUrl =
        screenMode === 'live'
          ? getLivePhotoUrl(rawEmployeePhotoUrls.get(ts.employeeId))
          : getPhotoUrl(ts.employeeName, ts.employeeId, employee?.photoUrl);

      // ── Delegation tagging (FE-14) ──────────────────────────────────────
      // A timesheet belongs to a delegated queue when the manager it was
      // originally assigned to has an active delegation to the current user.
      // In live mode: ts.approverId is the delegating manager's employee ID.
      // In mock mode: ts.delegatedApproverId carries the delegating manager's ID.
      // We match against activeDelegations using string comparison.
      const matchingDelegation = activeDelegations.find((d) => {
        const tsManagerId = ts.delegatedApproverId ?? ts.approverId;
        return tsManagerId !== undefined && String(tsManagerId) === String(d.managerId);
      });

      return {
        timesheet: ts,
        employeeName: ts.employeeName,
        department: employee?.department ?? '',
        weekEnding: ts.weekEnding,
        totalHours,
        policyViolations,
        anomalyResult,
        isOverdue,
        photoUrl,
        delegationManagerId: matchingDelegation?.managerId,
        delegationManagerName: matchingDelegation?.managerName,
      };
    });
  }, [filteredTimesheets, employees, timesheetAnomalies, timesheetPolicyViolations, screenMode, rawEmployeePhotoUrls, activeDelegations]);

  // selectedIds kept in sync via handleSelectionChange for downstream use (bulk confirm dialog etc.)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Approvals"
          size={36}
          showBreadcrumb={false}
          icon={<ClipboardCheck />}
        />
        <FeatureFeedbackCollector feature="Approval Queue" />
      </div>



      {/* KPI Summary Bar */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {/* Card 1: Total Pending */}
        <NavigationMetricCardAlt
          title="Pending Approvals"
          value={summary.pending.toString()}
          icon={Clock}
          variant={summary.pending === 0 ? 'success' : summary.overdue > 0 ? 'error' : 'warning'}
          change={summary.pending === 0 ? 'All up to date' : 'Awaiting review'}
        />

        {/* Card 2: Overdue — clicking activates the overdue filter */}
        <NavigationMetricCardAlt
          title="Overdue"
          value={summary.overdue.toString()}
          icon={AlertTriangle}
          variant={summary.overdue === 0 ? 'success' : 'error'}
          change={summary.overdue === 0 ? 'None overdue' : 'Needs attention'}
          onClick={summary.overdue > 0 ? () => setOverdueFilter(f => !f) : undefined}
          ariaLabel="Filter to overdue timesheets"
        />

        {/* Card 3: With Anomalies — clicking filters to anomaly rows */}
        <NavigationMetricCardAlt
          title="With Anomalies"
          value={summary.withAnomalies.toString()}
          icon={AlertCircle}
          variant={summary.withAnomalies === 0 ? 'success' : 'warning'}
          change={summary.withAnomalies === 0 ? 'No issues found' : 'Flagged for review'}
          onClick={summary.withAnomalies > 0 ? () => setAnomalyFilter(anomalyFilter === 'with-anomalies' ? 'all' : 'with-anomalies') : undefined}
          ariaLabel="Filter to timesheets with anomalies"
        />

        {/* Card 4: High Severity Issues */}
        <NavigationMetricCardAlt
          title="High Severity"
          value={summary.highSeverity.toString()}
          icon={ShieldAlert}
          variant={summary.highSeverity === 0 ? 'success' : 'error'}
          change={summary.highSeverity === 0 ? 'No critical issues' : 'Require immediate action'}
        />

        {/* Card 5: Total Hours Pending */}
        <NavigationMetricCardAlt
          title="Hours Pending"
          value={`${summary.totalHours.toFixed(1)}h`}
          icon={Timer}
          variant="info"
          change="Total hours to approve"
        />
      </div>

      {/* Data Grid */}
      {screenMode === 'live' && isLoadingPending ? (
        <Card>
          <CardHeader className="pb-0">
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 pb-3 px-6">
            <DashboardLoadingState message="Loading approvals..." />
          </CardContent>
        </Card>
      ) : (
        /* ── Flat approvals queue — delegated rows show "Delegated From" column ── */
        <Card>
          <CardHeader className="pb-0">
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 pb-3 px-6">
            <ApprovalGrid
              rows={approvalRows}
              onSelectionChange={handleSelectionChange}
              clearSelection={clearSelection}
              onView={setViewingTimesheet}
              onApprove={handleApproveClick}
              onReject={setRejectingTimesheet}
              canApproveReject={canApproveReject}
              canViewPolicyViolations={canViewPolicyViolations}
              canViewDetectedIssues={canViewDetectedIssues}
              currentEmployeeId={currentEmployee.id}
              pendingCount={pendingTimesheets.length}
              overtimeEnabled={organizationSettings?.overtime?.enabled === true}
              overtimeTypes={apiActiveOvertimeTypes}
              customFilters={
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-[160px] h-9">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={anomalyFilter} onValueChange={setAnomalyFilter}>
                    <SelectTrigger className="w-[140px] h-9">
                      <SelectValue placeholder="Issues" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="with-anomalies">With Issues</SelectItem>
                      <SelectItem value="clean">No Issues</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant={overdueFilter ? 'destructive' : 'outline'}
                    size="sm"
                    onClick={() => setOverdueFilter(f => !f)}
                    className="gap-1.5 h-9 whitespace-nowrap"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    Overdue
                    {summary.overdue > 0 && (
                      <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none ${
                        overdueFilter ? 'bg-white/20 text-white' : 'bg-destructive/15 text-destructive'
                      }`}>
                        {summary.overdue}
                      </span>
                    )}
                  </Button>
                </div>
              }
              toolbarActions={
                selectedIds.size > 0 ? (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleBulkApproveClick}
                      disabled={!canBulkApprove()}
                      title={!canBulkApprove() ? "You don't have permission to bulk approve" : undefined}
                      size="sm"
                      className="gap-1.5"
                    >
                      <Check className="h-4 w-4" />
                      Approve Selected ({selectedIds.size})
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleBulkRejectClick}
                      disabled={!canApproveReject()}
                      title={!canApproveReject() ? "You don't have permission to reject timesheets" : undefined}
                      size="sm"
                      className="gap-1.5"
                    >
                      <X className="h-4 w-4" />
                      Reject Selected ({selectedIds.size})
                    </Button>
                  </div>
                ) : null
              }
              filterChips={
                (departmentFilter !== 'all' || anomalyFilter !== 'all' || overdueFilter) ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {departmentFilter !== 'all' && (
                      <Badge
                        variant="secondary"
                        className="gap-1 cursor-pointer pr-1"
                        onClick={() => setDepartmentFilter('all')}
                      >
                        Dept: {departmentFilter}
                        <X className="h-3 w-3 ml-0.5" />
                      </Badge>
                    )}
                    {anomalyFilter !== 'all' && (
                      <Badge
                        variant="secondary"
                        className="gap-1 cursor-pointer pr-1"
                        onClick={() => setAnomalyFilter('all')}
                      >
                        {anomalyFilter === 'with-anomalies' ? 'With Issues' : 'No Issues'}
                        <X className="h-3 w-3 ml-0.5" />
                      </Badge>
                    )}
                    {overdueFilter && (
                      <Badge
                        variant="destructive"
                        className="gap-1 cursor-pointer pr-1"
                        onClick={() => setOverdueFilter(false)}
                      >
                        Overdue Only
                        <X className="h-3 w-3 ml-0.5" />
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setDepartmentFilter('all');
                        setAnomalyFilter('all');
                        setOverdueFilter(false);
                      }}
                    >
                      Clear all
                    </Button>
                  </div>
                ) : null
              }
            />
          </CardContent>
        </Card>
      )}

      {/* View Timesheet Dialog */}
      <Dialog open={!!viewingTimesheet} onOpenChange={(open) => { if (!open) { setShowIssuesSheet(false); handleDialogCloseAttempt(() => setViewingTimesheet(null)); } }}>
        <DialogContent className="max-w-7xl h-[95vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              {viewingTimesheet && (
                <EmployeeAvatar 
                  name={viewingTimesheet.employeeName}
                  photoUrl={screenMode === 'live' ? getLivePhotoUrl(rawEmployeePhotoUrls.get(viewingTimesheet.employeeId)) : getPhotoUrl(viewingTimesheet.employeeName, viewingTimesheet.employeeId, employees.find(e => e.id === viewingTimesheet.employeeId)?.photoUrl)}
                  size="lg"
                />
              )}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="truncate min-w-0 flex-shrink">Timesheet - {viewingTimesheet?.employeeName}</span>
                <div className="flex items-center gap-2 flex-shrink-0 ml-auto mr-6">
                {viewingTimesheet && (() => {
                  const violations = canViewPolicyViolations()
                    ? (timesheetPolicyViolations.get(viewingTimesheet.id) || [])
                    : [];
                  const anomalies = canViewDetectedIssues()
                    ? (timesheetAnomalies.get(viewingTimesheet.id)?.anomalies || [])
                    : [];
                  const totalIssues = violations.length + anomalies.length;
                  if (totalIssues === 0) return null;
                  const hasHigh = violations.some(v => v.severity === 'high') || anomalies.some(a => a.severity === 'high');
                  const hasMedium = violations.some(v => v.severity === 'medium') || anomalies.some(a => a.severity === 'medium');
                  const btnColour = hasHigh
                    ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30'
                    : hasMedium
                    ? 'border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30'
                    : 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950/30';
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowIssuesSheet(true)}
                      className={`h-7 px-2.5 text-xs font-medium gap-1.5 ${btnColour}`}
                    >
                      <Lightbulb className="h-3.5 w-3.5" />
                      Issues ({totalIssues})
                    </Button>
                  );
                })()}
                {isEditMode && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200">
                    Editing
                  </Badge>
                )}
                {canEditTimesheetInApproval() && !isEditMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                    className="shrink-0"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Timesheet
                  </Button>
                )}
                </div>
              </div>
            </DialogTitle>
            <DialogDescription className="ml-[60px] flex items-center gap-3 flex-wrap">
              <span>Week ending {viewingTimesheet && format(viewingTimesheet.weekEnding, 'dd MMM yyyy')}</span>
              {viewingTimesheet?.currentStageName && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <StageBadge
                    name={viewingTimesheet.currentStageName}
                    order={viewingTimesheet.currentStageOrder}
                    total={viewingTimesheet.totalStages}
                  />
                </>
              )}
              {viewingTimesheet?.submittedAt ? (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="text-muted-foreground text-sm">
                    Submitted {format(viewingTimesheet.submittedAt, 'dd MMM yyyy')}
                  </span>
                  <WaitingBadge submittedAt={viewingTimesheet.submittedAt} />
                </>
              ) : (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="text-muted-foreground text-sm">Submission date unknown</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1">
            {isEditMode && (
              <div className="sticky top-0 z-10 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 font-medium shadow-sm">
                <span>✏️ Edit mode active — make your changes then click <strong>Save Changes</strong> in the footer.</span>
              </div>
            )}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 text-left">Project</th>
                      <th className="p-2 text-left">Cost Centre</th>
                      {orgSettings?.showRole && (
                        <th className="p-2 text-left">
                          Role{orgSettings?.requireRole && <span className="text-destructive ml-0.5">*</span>}
                        </th>
                      )}
                      <th className="p-2 text-left">Description</th>
                      {DAYS.map((day, i) => (
                        <th
                          key={day}
                          className={cn(
                            'p-2 text-center',
                            isWeekendDay(day) && 'bg-muted'
                          )}
                        >
                          {DAY_LABELS[i]}
                        </th>
                      ))}
                      {organizationSettings?.overtime?.enabled && (
                        <th className="p-2 text-left">Overtime Type</th>
                      )}
                      <th className="p-2 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewingTimesheet?.entries ?? []).map(entry => {
                      // Merge edits over original values for display
                      const entryEdits = editedEntries[entry.id] || {};
                      const effectiveProjectId = entryEdits.projectId ?? entry.projectId;
                      const effectiveCostCentreId = entryEdits.costCentreId ?? entry.costCentreId;
                      const effectiveDescription = entryEdits.description ?? entry.description;
                      const effectiveRoleId = 'roleId' in entryEdits ? entryEdits.roleId : entry.roleId;
                      const effectiveRoleName = 'roleName' in entryEdits ? entryEdits.roleName : entry.roleName;
                      const effectiveHours = entryEdits.hours ?? entry.hours;

                      const project = projects.find(p => p.id === effectiveProjectId);
                      const costCentre = project?.costCentres.find(cc => cc.id === effectiveCostCentreId);
                      const total = Object.values(effectiveHours).reduce((sum, h) => sum + h, 0);
                      const isExpanded = expandedEntryIds.has(entry.id);
                      const showExpansion = !isEditMode && shouldShowExpansionIcon(effectiveDescription);
                      const truncatedDescription = effectiveDescription.length > 100 
                        ? effectiveDescription.substring(0, 100) + '...' 
                        : effectiveDescription;
                      
                      // Check if this is an absence entry — always locked from editing
                      const isAbsence = entry.sourceIntegration === 'people-hr' || entry.isReadOnly === true;
                      const absenceType: AbsenceType | null = isAbsence 
                        ? (entry.description?.toLowerCase().replace(' ', '-') as AbsenceType)
                        : null;
                      
                      // Whether this row is editable
                      const isRowEditable = isEditMode && !isAbsence;

                      // Calculate variance for scheduled bookings
                      const variance = entry.isFromSchedule && !entry.isDismissed
                        ? calculateEntryVariance(entry)
                        : null;
                      
                      return (
                        <React.Fragment key={entry.id}>
                          <tr className={`border-b transition-colors ${isAbsence ? 'bg-purple-50/30' : ''} ${isRowEditable ? 'bg-amber-50/30 dark:bg-amber-950/10' : 'hover:bg-muted/30'}`}>
                            {/* Project cell */}
                            <td className="p-2">
                              {isRowEditable ? (
                                <div className="w-[220px]">
                                  <ProjectSelector
                                    projects={projects.filter(p => p.active)}
                                    value={effectiveProjectId}
                                    onChange={(val) => handleEntryProjectChange(entry.id, val)}
                                    showFavourites={false}
                                    placeholder="Select project..."
                                  />
                                </div>
                              ) : (
                                  <div className="flex flex-col gap-1">
                                    <div className="text-xs truncate max-w-[200px]">
                                      {project ? `${project.name} (${project.code})` : entry.projectName ? `${entry.projectName}${entry.projectCode ? ` (${entry.projectCode})` : ''}` : '-'}
                                    </div>
                                    {isAbsence && <AbsenceIndicator />}
                                  </div>
                              )}
                            </td>
                            {/* Cost Centre cell */}
                            <td className="p-2">
                              {isRowEditable ? (
                                <div className="w-[200px]">
                                  <CostCentreSelector
                                    costCentres={project?.costCentres ?? []}
                                    value={effectiveCostCentreId}
                                    onChange={(val) => handleEntryFieldChange(entry.id, 'costCentreId', val)}
                                    disabled={!project || (project.costCentres?.length ?? 0) === 0}
                                    showFavourites={false}
                                    placeholder="Select cost centre..."
                                  />
                                </div>
                              ) : (
                                <div className="text-xs truncate max-w-[150px]">
                                  {costCentre ? `${costCentre.name} (${costCentre.code})` : entry.costCentreName ? `${entry.costCentreName}${entry.costCentreCode ? ` (${entry.costCentreCode})` : ''}` : '-'}
                                </div>
                              )}
                            </td>
                            {/* Role cell */}
                            {orgSettings?.showRole && (
                              <td className="p-2">
                                {isRowEditable ? (
                                  <div className="w-[160px]">
                                    <RoleSelector
                                      value={effectiveRoleId}
                                      onChange={(roleId, roleName) => handleEntryRoleChange(entry.id, roleId, roleName)}
                                      disabled={isAbsence}
                                      required={orgSettings?.requireRole}
                                      screenMode={screenMode}
                                      displayName={effectiveRoleName}
                                    />
                                  </div>
                                ) : (
                                  <div className="text-xs truncate max-w-[150px]">
                                    {effectiveRoleName ?? '-'}
                                  </div>
                                )}
                              </td>
                            )}
                            {/* Description cell */}
                            <td className="p-2">
                              {isRowEditable ? (
                                <Input
                                  value={effectiveDescription}
                                  onChange={(e) => handleEntryFieldChange(entry.id, 'description', e.target.value)}
                                  className="h-7 text-xs w-[220px]"
                                  placeholder="Description..."
                                />
                              ) : (
                                <div className="flex flex-col gap-1.5">
                                  <div className="flex items-center gap-2 max-w-[280px]">
                                    {showExpansion && (
                                      <button
                                        onClick={() => toggleEntryExpansion(entry.id)}
                                        className="flex-shrink-0 hover:bg-muted rounded p-0.5 transition-colors"
                                        aria-label={isExpanded ? 'Collapse description' : 'Expand description'}
                                        aria-expanded={isExpanded}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                        ) : (
                                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                        )}
                                      </button>
                                    )}
                                    <TooltipProvider delayDuration={300}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="text-xs flex-1 min-w-0">
                                            {!isExpanded && showExpansion ? (
                                              <span className="line-clamp-1">{truncatedDescription}</span>
                                            ) : (
                                              <span className={showExpansion ? '' : 'line-clamp-2'}>{effectiveDescription || '-'}</span>
                                            )}
                                          </div>
                                        </TooltipTrigger>
                                        {effectiveDescription && effectiveDescription.length > 50 && (
                                          <TooltipContent side="top" className="max-w-md">
                                            <p className="text-xs whitespace-pre-wrap">{effectiveDescription}</p>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </div>
                              )}
                            </td>
                            {/* Hours cells — full Excel-like inputs matching TimesheetGrid */}
                            {DAYS.map(day => {
                              const cellKey = getHoursCellKey(entry.id, day);
                              const isEditing = editingHours[cellKey] !== undefined;
                              const displayValue = isEditing
                                ? editingHours[cellKey]!
                                : effectiveHours[day] === 0
                                  ? ''
                                  : effectiveHours[day].toFixed(2);
                              const weekend = isWeekendDay(day);
                              return (
                                <td key={day} className={cn('p-2 text-center', weekend && 'bg-muted/30')}>
                                  {isRowEditable ? (
                                    <Input
                                      type="text"
                                      inputMode="decimal"
                                      value={displayValue}
                                      placeholder="0"
                                      onChange={(e) => {
                                        setEditingHours(prev => ({ ...prev, [cellKey]: e.target.value }));
                                      }}
                                      onFocus={(e) => e.target.select()}
                                      onBlur={(e) => {
                                        const raw = e.target.value;
                                        let hours = raw === '' ? 0 : parseFloat(raw);
                                        if (isNaN(hours)) hours = 0;
                                        // Apply time rounding
                                        const timeRounding = orgSettings?.timeRounding ?? 'none';
                                        let step = 0;
                                        if (timeRounding === '15min') step = 0.25;
                                        else if (timeRounding === '30min') step = 0.5;
                                        else if (timeRounding === '1hour') step = 1;
                                        if (step > 0) hours = Math.round(hours / step) * step;
                                        // Enforce minimum entry duration
                                        const minSetting = orgSettings?.minimumEntryDuration ?? '6min';
                                        let min = 0.1;
                                        if (minSetting === '15min') min = 0.25;
                                        else if (minSetting === '30min') min = 0.5;
                                        else if (minSetting === '1hour') min = 1;
                                        if (hours !== 0 && Math.abs(hours) < min) {
                                          hours = hours < 0 ? -min : min;
                                        }
                                        // Clamp based on allowNegativeHours
                                        const allowNeg = orgSettings?.allowNegativeHours ?? false;
                                        const clamped = allowNeg
                                          ? Math.max(-24, Math.min(24, hours))
                                          : Math.max(0, Math.min(24, hours));
                                        handleEntryHoursChange(entry.id, day, String(clamped));
                                        setEditingHours(prev => {
                                          const next = { ...prev };
                                          delete next[cellKey];
                                          return next;
                                        });
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          e.currentTarget.blur();
                                        }
                                        if (e.key === 'ArrowUp') {
                                          e.preventDefault();
                                          const current = parseFloat(displayValue) || 0;
                                          setEditingHours(prev => ({ ...prev, [cellKey]: Math.min(24, current + 0.5).toFixed(1) }));
                                        }
                                        if (e.key === 'ArrowDown') {
                                          e.preventDefault();
                                          const current = parseFloat(displayValue) || 0;
                                          setEditingHours(prev => ({ ...prev, [cellKey]: Math.max(0, current - 0.5).toFixed(1) }));
                                        }
                                      }}
                                      className={cn(
                                        'text-center text-sm h-9 px-1 sm:px-2',
                                        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                                        effectiveHours[day] > 0 ? 'text-foreground' : 'text-muted-foreground/40',
                                        isAbsence && 'bg-muted/50 cursor-not-allowed',
                                        !isAbsence && 'hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-text',
                                        isAbsence && effectiveHours[day] > 0 && 'bg-purple-50/40 border-purple-200'
                                      )}
                                      disabled={isAbsence}
                                    />
                                  ) : (
                                    weekend && effectiveHours[day] > 0 ? (
                                      <span className="text-orange-600 font-semibold text-sm">
                                        {effectiveHours[day].toFixed(2)}
                                      </span>
                                    ) : (
                                      <span className="text-sm">
                                        {effectiveHours[day] > 0 ? effectiveHours[day].toFixed(2) : '-'}
                                      </span>
                                    )
                                  )}
                                </td>
                              );
                            })}
                            {organizationSettings?.overtime?.enabled && (
                              <td className="p-2">
                                {entry.overtimeTypeId ? (
                                  <OvertimeTypeBadge
                                    name={entry.overtimeTypeName ?? entry.overtimeTypeId}
                                    colour={(organizationSettings.overtime.types ?? []).find(t => t.id === entry.overtimeTypeId)?.badgeColour ?? '#6b7280'}
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                            )}
                            <td className="p-2 text-center font-semibold">{total.toFixed(2)}</td>
                          </tr>
                          
                          {/* Variance Display for Scheduled Bookings */}
                          {entry.isFromSchedule && !entry.isDismissed && variance && Math.abs(variance.percentage) > 10 && (
                            <tr className="border-b bg-blue-50/50 dark:bg-blue-950/20">
                              <td colSpan={11} className="p-2">
                                <div className="flex items-center gap-3 text-xs">
                                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                    <Calendar className="h-3 w-3 mr-1 text-amber-500" />
                                    Scheduled Booking
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    Scheduled: {variance.scheduled}h
                                  </span>
                                  <span className="text-muted-foreground">|</span>
                                  <span className="text-muted-foreground">
                                    Logged: {variance.actual}h
                                  </span>
                                  <span className="text-muted-foreground">|</span>
                                  <Badge variant={getVarianceBadgeVariant(variance.severity)}>
                                    {variance.variance > 0 ? '+' : ''}{variance.variance}h ({variance.variance > 0 ? '+' : ''}{variance.percentage}%)
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 ml-auto"
                                    onClick={() => setViewingBookingDetails({
                                      ...entry,
                                      projectName: project?.name || '',
                                      costCentreName: costCentre?.name || '',
                                    })}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Details
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                          
                          {/* Dismissal Display for Dismissed Bookings */}
                          {entry.isDismissed && (
                            <tr className="border-b bg-orange-50/50 dark:bg-orange-950/20">
                              <td colSpan={11} className="p-2">
                                <div className="flex items-center gap-3 text-xs">
                                  <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                                    <Ban className="h-3 w-3 mr-1 text-muted-foreground" />
                                    Dismissed Booking
                                  </Badge>
                                  <span className="text-muted-foreground">
                                    {entry.dismissalReason ? `"${entry.dismissalReason}"` : 'No reason provided'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 ml-auto"
                                    onClick={() => setViewingBookingDetails({
                                      ...entry,
                                      projectName: project?.name || '',
                                      costCentreName: costCentre?.name || '',
                                    })}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Details
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                          
                          {isExpanded && showExpansion && (
                            <tr className="border-b bg-muted/20">
                              <td colSpan={11} className="p-3">
                                <div className="text-xs">
                                  <div className="font-medium text-muted-foreground mb-1">Full Description:</div>
                                  <div className="text-foreground whitespace-pre-wrap bg-background p-3 rounded border">
                                    {entry.description}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Daily Totals Row — live-recalculates as hours are edited */}
                    {computedDailyTotals && (
                      <tr className="border-t-2 border-t-primary bg-accent-teal-50/50 dark:bg-accent-teal-950/20 font-semibold">
                        {/* Span the same fixed left columns as the header: Project + Cost Centre + (Role?) + Description */}
                        <td colSpan={orgSettings?.showRole ? 4 : 3} className="p-2 text-left">
                          <span className="font-bold">Daily Totals</span>
                        </td>
                        {DAYS.map(day => {
                          const weekend = isWeekendDay(day);
                          return (
                            <td key={day} className={cn('p-2 text-center', weekend && 'bg-muted/30')}>
                              {computedDailyTotals[day] > 0 ? (
                                weekend ? (
                                  <span className="text-orange-600 font-bold">{computedDailyTotals[day].toFixed(2)}</span>
                                ) : (
                                  computedDailyTotals[day].toFixed(2)
                                )
                              ) : '-'}
                            </td>
                          );
                        })}
                        <td className="p-2 text-center font-bold text-accent-teal-900">
                          {DAYS.reduce((s, day) => s + (computedDailyTotals[day] ?? 0), 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 flex-shrink-0 border-t pt-4">
            <div className="flex gap-2 flex-1">
              <Button
                variant="outline"
                onClick={() => handleDialogCloseAttempt(() => setViewingTimesheet(null))}
              >
                Close
              </Button>
              {viewingTimesheet?.id && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => setShowAuditHistory(true)}
                        aria-label="View timesheet history"
                      >
                        <History className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">View History</span>
                        <span className="sm:hidden">History</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View complete audit trail and change history</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {/* Edit mode actions */}
            {isEditMode ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdits}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdits}
                  disabled={isSavingEdits || !hasUnsavedChanges}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingEdits ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          if (viewingTimesheet) {
                            setRejectingTimesheet(viewingTimesheet);
                            setViewingTimesheet(null);
                          }
                        }}
                        disabled={!canApproveReject() || (viewingTimesheet?.cannotApproveOwnTimesheet === true && viewingTimesheet?.employeeId === currentEmployee.id)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canApproveReject() && (
                    <TooltipContent><p>You don't have permission to reject timesheets</p></TooltipContent>
                  )}
                  {(viewingTimesheet?.cannotApproveOwnTimesheet === true && viewingTimesheet?.employeeId === currentEmployee.id) && (
                    <TooltipContent><p>Cannot reject own timesheet</p></TooltipContent>
                  )}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        onClick={() => {
                          if (viewingTimesheet) {
                            handleApproveClick(viewingTimesheet, { fromViewDialog: true });
                          }
                        }}
                        disabled={!canApproveReject() || (viewingTimesheet?.cannotApproveOwnTimesheet === true && viewingTimesheet?.employeeId === currentEmployee.id)}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!canApproveReject() && (
                    <TooltipContent><p>You don't have permission to approve timesheets</p></TooltipContent>
                  )}
                  {(viewingTimesheet?.cannotApproveOwnTimesheet === true && viewingTimesheet?.employeeId === currentEmployee.id) && (
                    <TooltipContent><p>Cannot approve own timesheet</p></TooltipContent>
                  )}
                </Tooltip>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Issues Sheet — opens over the View Timesheet dialog */}
      {viewingTimesheet && (() => {
        // Respect per-role visibility permissions — only include data the user is allowed to see.
        const violations = canViewPolicyViolations()
          ? (timesheetPolicyViolations.get(viewingTimesheet.id) || [])
          : [];
        const anomalies = canViewDetectedIssues()
          ? (timesheetAnomalies.get(viewingTimesheet.id)?.anomalies || [])
          : [];
        const ANOMALY_TYPE_MAP: Record<string, AnomalyWarning['type']> = {
          'daily-hours-exceeded': 'overtime-risk',
          'weekly-hours-exceeded': 'overtime-risk',
          'weekend-work-no-approval': 'weekend-work',
          'missing-description': 'duplicate-entry',
          'missing-activity-type': 'duplicate-entry',
          'hours-above-average': 'unusual-pattern',
          'unusual-project-allocation': 'unusual-pattern',
          'late-submission': 'missing-time',
          'first-project-entry': 'closed-project',
          'scheduled-booking-variance': 'unusual-pattern',
          'scheduled-booking-dismissed': 'unusual-pattern',
          'time-and-absence-conflict': 'time-and-absence-conflict',
        };
        const ANOMALY_POLICY_MAP: Record<string, string> = {
          'daily-hours-exceeded': 'daily-overage',
          'weekly-hours-exceeded': 'daily-overage',
          'weekend-work-no-approval': 'non-working-day',
          'missing-description': 'missing-hours',
          'missing-activity-type': 'missing-hours',
          'hours-above-average': 'daily-overage',
          'unusual-project-allocation': 'unusual-pattern',
          'late-submission': 'missing-hours',
          'first-project-entry': 'unusual-pattern',
          'scheduled-booking-variance': 'missing-hours',
          'scheduled-booking-dismissed': 'missing-hours',
          'time-and-absence-conflict': 'missing-hours',
        };
        const unifiedWarnings: AnomalyWarning[] = [
          ...violations.map((v, i): AnomalyWarning => {
            const vt = v.violationType ?? '';
            const policyCheckType = vt.includes('legal') ? 'legal-maximum'
              : vt.includes('missing') ? 'missing-hours'
              : vt.includes('non-working') ? 'non-working-day'
              : 'daily-overage';
            const warningType: AnomalyWarning['type'] = vt.includes('legal') ? 'overtime-risk'
              : vt.includes('missing') ? 'missing-time'
              : vt.includes('non-working') ? 'weekend-work'
              : 'overtime-risk';
            return {
              id: `policy-${i}`,
              type: warningType,
              severity: v.severity,
              message: v.message,
              suggestion: v.details
                ? `Expected ${v.details.expected}h · Logged ${v.details.actual}h${v.details.limit ? ` · Limit ${v.details.limit}h` : ''}`
                : undefined,
              day: v.date,
              policyCheckType,
            };
          }),
          ...anomalies.map((a, i): AnomalyWarning => ({
            id: `anomaly-${i}`,
            type: ANOMALY_TYPE_MAP[a.type] ?? 'unusual-pattern',
            severity: a.severity,
            message: a.message,
            suggestion: a.details,
            day: a.day,
            policyCheckType: ANOMALY_POLICY_MAP[a.type] ?? 'data-quality',
          })),
        ];
        // Filter warnings by the org-configured minimum severity
        const minSeverity = orgSettings.approvalScreen?.minSeverityToShow ?? 'all';
        const SEVERITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2 };
        const minRank = minSeverity === 'high' ? 2 : minSeverity === 'medium' ? 1 : 0;
        const filteredWarnings = minRank === 0
          ? unifiedWarnings
          : unifiedWarnings.filter(w => (SEVERITY_RANK[w.severity] ?? 0) >= minRank);

        // Overtime summary — only when overtime tracking is enabled for this org
        const overtimeEnabled = organizationSettings?.overtime?.enabled === true;
        const overtimeInsight: PersonalInsight | null = overtimeEnabled
          ? aiService.generateOvertimeInsight(viewingTimesheet)
          : null;

        return (
          <AIInsightsSheet
            open={showIssuesSheet}
            onOpenChange={(open) => setShowIssuesSheet(open)}
            warnings={filteredWarnings}
            overtimeInsight={overtimeInsight}
          />
        );
      })()}

      {/* Reject Dialog */}
      <Dialog open={!!rejectingTimesheet} onOpenChange={() => { setRejectingTimesheet(null); setRejectionReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet{rejectingTimesheet?.employeeName ? ` — ${rejectingTimesheet.employeeName}` : ''}</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this timesheet
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Your reason will be sent directly to the employee. Please be specific about what needs to be corrected.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[
                'Incorrect project code',
                'Missing entry descriptions',
                'Hours don\'t match schedule',
                'Non-billable time on billable project',
                'Duplicate entries',
              ].map((reason) => (
                <Badge
                  key={reason}
                  variant="outline"
                  className="cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => {
                    const separator = rejectionReason.trim() ? '. ' : '';
                    const newValue = (rejectionReason.trim() + separator + reason).slice(0, REJECTION_MAX_CHARS);
                    setRejectionReason(newValue);
                  }}
                >
                  {reason}
                </Badge>
              ))}
            </div>
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value.slice(0, REJECTION_MAX_CHARS))}
              rows={4}
            />
            <div className="flex items-center justify-between text-xs">
              <span className={(!requiresRejectionReason() || rejectionMeetsMinimum) ? 'text-muted-foreground invisible' : 'text-muted-foreground'}>
                {rejectionCharCount === 0
                  ? `Minimum ${REJECTION_MIN_CHARS} characters required`
                  : `${rejectionCharsRemaining} more character${rejectionCharsRemaining === 1 ? '' : 's'} needed`}
              </span>
              <span className={cn(
                'font-medium',
                rejectionCharCount >= REJECTION_MAX_CHARS * 0.9
                  ? 'text-destructive'
                  : (!requiresRejectionReason() || rejectionMeetsMinimum)
                    ? 'text-green-600'
                    : 'text-amber-500'
              )}>
                {rejectionCharCount} / {REJECTION_MAX_CHARS}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setRejectingTimesheet(null);
              setRejectionReason('');
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={rejectTimesheet}
              disabled={rejectionCharCount === 0 || (requiresRejectionReason() && !rejectionMeetsMinimum)}
            >
              Reject Timesheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F-02: Single-Item Approve Confirmation for Flagged Timesheets */}
      <AlertDialog
        open={!!pendingFlaggedApproval}
        onOpenChange={(open) => { if (!open) setPendingFlaggedApproval(null); }}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Approve Flagged Timesheet?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingFlaggedApproval?.employeeName}'s timesheet has the following issues. Please review them before approving.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pendingFlaggedApproval && (
            <FlaggedIssuesList
              anomalyResult={canViewDetectedIssues()
                ? timesheetAnomalies.get(pendingFlaggedApproval.id)
                : undefined}
              policyViolations={canViewPolicyViolations()
                ? (timesheetPolicyViolations.get(pendingFlaggedApproval.id) || [])
                : []}
            />
          )}

          <div className="space-y-2 my-2">
            <label className="text-sm font-medium">Add an approval note (optional)</label>
            <Textarea
              placeholder="e.g. Approved — all project codes verified."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value.slice(0, 500))}
              rows={2}
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              {approvalNotes.length} / 500
            </div>
          </div>

          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => { setPendingFlaggedApproval(null); setApprovalNotes(''); }}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                const ts = pendingFlaggedApproval;
                setPendingFlaggedApproval(null);
                setApprovalNotes('');
                if (ts) setViewingTimesheet(ts);
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Review Timesheet
            </Button>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const ts = pendingFlaggedApproval;
                const notes = approvalNotes;
                setPendingFlaggedApproval(null);
                setApprovalNotes('');
                if (ts) approveTimesheet(ts, notes);
              }}
            >
              <Check className="h-4 w-4 mr-2" />
              Approve Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Approval Confirmation Dialog */}
      <AlertDialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve {selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''}. 
              Please review the details below before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 my-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground">Total Timesheets</div>
                <div className="text-2xl font-bold">{selectedTimesheets.length}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Hours</div>
                <div className="text-2xl font-bold">
                  {selectedTimesheets.reduce((sum, ts) => sum + calculateTimesheetTotal(ts), 0).toFixed(1)}h
                </div>
              </div>
            </div>

            {/* Anomaly Warning */}
            {selectedWithAnomalies.length > 0 && (
              <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    Warning: {selectedWithAnomalies.length} Timesheet{selectedWithAnomalies.length !== 1 ? 's' : ''} with Issues
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    The following timesheets have detected anomalies. Please review them carefully:
                  </p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedWithAnomalies.slice(0, 10).map(ts => {
                      const anomalyResult = timesheetAnomalies.get(ts.id);
                      const highSeverityCount = anomalyResult?.anomalies.filter(a => a.severity === 'high').length || 0;
                      
                      return (
                        <div key={ts.id} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                          <span className="font-medium">{ts.employeeName}</span>
                          <div className="flex gap-1">
                            {highSeverityCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {highSeverityCount} High
                              </Badge>
                            )}
                            <AnomalyIndicator 
                              anomalyResult={anomalyResult!}
                              size="sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                    {selectedWithAnomalies.length > 10 && (
                      <div className="text-xs text-muted-foreground text-center pt-2">
                        ...and {selectedWithAnomalies.length - 10} more
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employee List */}
            <div>
              <div className="text-sm font-medium mb-2">Affected Employees:</div>
              <div className="max-h-[150px] overflow-y-auto p-3 bg-muted/30 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedTimesheets.map(ts => (
                    <div key={ts.id} className="flex items-center gap-2">
                      <Check className="h-3 w-3 text-green-600" />
                      <span>{ts.employeeName}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Approval Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Add an approval note (optional)</label>
              <Textarea
                placeholder="e.g. Approved — all project codes verified."
                value={bulkApprovalNotes}
                onChange={(e) => setBulkApprovalNotes(e.target.value.slice(0, 500))}
                rows={2}
              />
              <div className="flex justify-end text-xs text-muted-foreground">
                {bulkApprovalNotes.length} / 500
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowBulkConfirmDialog(false);
              setPendingBulkAction(null);
              setBulkApprovalNotes('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkApprove}>
              <Check className="h-4 w-4 mr-2" />
              Approve All {selectedTimesheets.length} Timesheets
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* F-12: Bulk Reject Confirmation Dialog */}
      <AlertDialog open={showBulkRejectDialog} onOpenChange={(open) => { if (!open) { setShowBulkRejectDialog(false); setBulkRejectReason(''); } }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <X className="h-5 w-5 text-destructive" />
              Reject {selectedTimesheets.length} Timesheet{selectedTimesheets.length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to reject {selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''}.
              Please provide a reason below.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 my-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground">Total Timesheets</div>
                <div className="text-2xl font-bold">{selectedTimesheets.length}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Hours</div>
                <div className="text-2xl font-bold">
                  {selectedTimesheets.reduce((sum, ts) => sum + calculateTimesheetTotal(ts), 0).toFixed(1)}h
                </div>
              </div>
            </div>

            {/* Employee List */}
            <div>
              <div className="text-sm font-medium mb-2">Affected Employees:</div>
              <div className="max-h-[150px] overflow-y-auto p-3 bg-muted/30 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedTimesheets.map(ts => (
                    <div key={ts.id} className="flex items-center gap-2">
                      <X className="h-3 w-3 text-destructive" />
                      <span>{ts.employeeName}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                This rejection reason will be sent to all {selectedTimesheets.length} employee{selectedTimesheets.length !== 1 ? 's' : ''}.
              </p>
            </div>

            {/* Rejection Reason */}
            <div className="space-y-2">
              <Textarea
                placeholder="Enter rejection reason..."
                value={bulkRejectReason}
                onChange={(e) => setBulkRejectReason(e.target.value.slice(0, BULK_REJECT_MAX_CHARS))}
                rows={4}
              />
              <div className="flex items-center justify-between text-xs">
                <span className={(!requiresRejectionReason() || bulkRejectMeetsMinimum) ? 'text-muted-foreground invisible' : 'text-muted-foreground'}>
                  {bulkRejectReason.length === 0
                    ? `Minimum ${BULK_REJECT_MIN_CHARS} characters required`
                    : `${bulkRejectCharsRemaining} more character${bulkRejectCharsRemaining === 1 ? '' : 's'} needed`}
                </span>
                <span className={cn(
                  'font-medium',
                  bulkRejectReason.length >= BULK_REJECT_MAX_CHARS * 0.9
                    ? 'text-destructive'
                    : (!requiresRejectionReason() || bulkRejectMeetsMinimum)
                      ? 'text-green-600'
                      : 'text-amber-500'
                )}>
                  {bulkRejectReason.length} / {BULK_REJECT_MAX_CHARS}
                </span>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowBulkRejectDialog(false); setBulkRejectReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={executeBulkReject}
              disabled={requiresRejectionReason() && !bulkRejectMeetsMinimum}
            >
              <X className="h-4 w-4 mr-2" />
              Reject {selectedTimesheets.length} Timesheet{selectedTimesheets.length !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit History Dialog */}
      {viewingTimesheet?.id && (
        <AuditHistoryDialog
          timesheetId={viewingTimesheet.id}
          employeeName={viewingTimesheet.employeeName}
          weekEnding={viewingTimesheet.weekEnding}
          open={showAuditHistory}
          onOpenChange={setShowAuditHistory}
          timesheet={viewingTimesheet}
        />
      )}

      {/* Booking Details Modal (for variance review) */}
      {viewingBookingDetails && (
        <BookingDetailsModal
          open={!!viewingBookingDetails}
          onOpenChange={(open) => !open && setViewingBookingDetails(null)}
          booking={viewingBookingDetails}
          showVariance={true}
        />
      )}

      {/* Unsaved Changes Guard Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits to this timesheet. If you close now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUnsavedChangesDialog(false)}>
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
